import { FormEvent, KeyboardEvent } from 'react';

import { TLD_LIST } from '../constants/tlds';

export const MAX_CHARACTER_LIMIT = 14000;

export const convertMinutesToHHMM = (minutes: number): string => {
  const sign = minutes < 0 ? '-' : '+';
  const absoluteMinutes = Math.abs(minutes);
  const hours = Math.floor(absoluteMinutes / 60);
  const remainingMinutes = absoluteMinutes % 60;
  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(remainingMinutes).padStart(2, '0');
  return `${sign}${formattedHours}:${formattedMinutes}`;
};

export const escapeCSVValue = (value: string | null | undefined) => {
  return (value ?? '').trim().replace(/^[-=+@]+/, (match) => `"${match}"`);
};

export const extractZodErrors = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: Record<string, any>,
  parentKey: string = '',
): { targetName: string; text: string }[] => {
  return Object.keys(errors).flatMap((key) => {
    const currentKey = parentKey ? `${parentKey}.${key}` : key;
    if (errors[key]?.message && typeof errors[key]?.message === 'string') {
      return {
        targetName: currentKey,
        text: errors[key]?.message,
      };
    } else if (typeof errors[key] === 'object') {
      return extractZodErrors(errors[key], currentKey);
    } else {
      return [];
    }
  });
};

export const formatAsGBP = (value: string | number | undefined | null): string => {
  if (typeof value === 'undefined' || value === null) {
    return '';
  }

  const numberValue = typeof value === 'number' ? value : parseFloat(value.replace(/,/g, ''));
  if (isNaN(numberValue)) {
    return '';
  }
  const roundedValue = Math.round(numberValue * 100) / 100;
  const isWholeNumber = Number.isInteger(roundedValue);
  const formattedValue = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: isWholeNumber ? 0 : 2,
    maximumFractionDigits: isWholeNumber ? 0 : 2,
  }).format(numberValue);

  return formattedValue;
};

export const formatDate = (date: Date | undefined, style: string = 'short', includeSuffix: boolean = false) => {
  if (!date) return;
  if (style === 'short') {
    return date.toLocaleDateString('en-GB', { timeZone: 'UTC', year: 'numeric', month: 'short', day: '2-digit' });
  } else if (style === 'long') {
    return date.toLocaleDateString('en-GB', { timeZone: 'UTC', year: 'numeric', month: 'long', day: 'numeric' });
  } else if (style === 'suffix') {
    const day = date.getUTCDate();
    const suffix = !includeSuffix
      ? ''
      : day % 10 === 1 && day !== 11
        ? 'st'
        : day % 10 === 2 && day !== 12
          ? 'nd'
          : day % 10 === 3 && day !== 13
            ? 'rd'
            : 'th';

    return `${day}${suffix} ${date.toLocaleString('en-GB', { timeZone: 'UTC', month: 'long' })} ${date.getUTCFullYear()}`;
  } else {
    return date.toLocaleDateString('en-GB', { timeZone: 'UTC' });
  }
};

export const formatDateAsUTC = (date: Date | undefined) => {
  if (typeof date === 'undefined') return;
  const tzoffset = date.getTimezoneOffset() * 60000;
  const utcDate = new Date(date.valueOf() - tzoffset).toISOString();
  return utcDate;
};

export const formatDateTime = (date: Date | undefined, style: string = 'short') => {
  if (typeof date === 'undefined') return;
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes} on ${formatDate(date, style)}`;
};

export const formatLocalDateTime = (iso?: string): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('en-GB', { month: 'short' });
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day} ${month} ${year} ${hours}:${minutes}`;
};

const shortMonths = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const toWallClock = (value: Date | string): Date | undefined => {
  if (typeof value === 'string') {
    const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
    if (iso) {
      const [year, month, day, hours = 0, minutes = 0] = iso.slice(1).map((part) => Number(part ?? 0));
      return new Date(Date.UTC(year, month - 1, day, hours, minutes));
    }
    const named = value.match(/^(\d{1,2}) ([A-Za-z]{3}) (\d{4})(?: (\d{1,2}):(\d{2}) ([AP]M))?$/i);
    if (!named) return;
    const month = shortMonths.indexOf(named[2].toLowerCase());
    if (month < 0) return;
    const hours12 = Number(named[4] ?? 0) % 12;
    const hours = named[6]?.toUpperCase() === 'PM' ? hours12 + 12 : hours12;
    return new Date(Date.UTC(Number(named[3]), month, Number(named[1]), hours, Number(named[5] ?? 0)));
  }
  if (Number.isNaN(value.getTime())) return;
  return new Date(
    Date.UTC(value.getFullYear(), value.getMonth(), value.getDate(), value.getHours(), value.getMinutes()),
  );
};

const utcOffsetMinutesAt = (instantMs: number, timeZone: string) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).formatToParts(new Date(instantMs));
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const localAsUTC = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'));
  return Math.round((localAsUTC - Math.floor(instantMs / 60000) * 60000) / 60000);
};

export const wallClockToInstant = (value: Date | string, timeZone: string | undefined): Date | undefined => {
  const wallClock = toWallClock(value);
  if (!wallClock) return;
  try {
    const zone = timeZone || 'Etc/UTC';
    const guess = utcOffsetMinutesAt(wallClock.getTime(), zone);
    const offset = utcOffsetMinutesAt(wallClock.getTime() - guess * 60000, zone);
    return new Date(wallClock.getTime() - offset * 60000);
  } catch {
    return wallClock;
  }
};

const formatTimeZoneLabel = (wallClock: Date, timezone: string) => {
  const labelled = timezone.match(/^(\S+) \(UTC [^)]*\)$/);
  const name = labelled ? labelled[1] : timezone;
  const display = name.replaceAll('_', ' ').replace('Etc/', '');
  if (!labelled) return display;
  try {
    const guess = utcOffsetMinutesAt(wallClock.getTime(), name);
    const offset = utcOffsetMinutesAt(wallClock.getTime() - guess * 60000, name);
    const abs = Math.abs(offset);
    const hhmm = `${Math.floor(abs / 60)
      .toString()
      .padStart(2, '0')}:${(abs % 60).toString().padStart(2, '0')}`;
    return `${display} (UTC ${offset < 0 ? '-' : '+'}${hhmm})`;
  } catch {
    return timezone.replaceAll('_', ' ').replace('Etc/', '');
  }
};

export const formatWallClockDate = (value: Date | string | undefined, style: string = 'short') => {
  if (value === undefined) return '';
  const wallClock = toWallClock(value);
  return wallClock ? formatDate(wallClock, style) : '';
};

export const formatDateTimeWithTimeZone = (value: Date | string, timezone: string, style: string = 'suffix') => {
  const wallClock = toWallClock(value);
  if (!wallClock) return '';
  const hours = wallClock.getUTCHours().toString().padStart(2, '0');
  const minutes = wallClock.getUTCMinutes().toString().padStart(2, '0');
  const formattedDate = `${hours}:${minutes} ${formatTimeZoneLabel(wallClock, timezone)} on ${formatDate(wallClock, style)}`;
  return formattedDate;
};

export const formatISODate = (date: Date, includeTime?: boolean) => {
  if (typeof date === 'undefined') return;
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  let hours = '00';
  let minutes = '00';
  if (includeTime) {
    hours = date.getHours().toString().padStart(2, '0');
    minutes = date.getMinutes().toString().padStart(2, '0');
  }
  return `${year}-${month}-${day}T${hours}:${minutes}:00.000Z`;
};

export const formatNanoseconds = (nanoseconds: number): string => {
  const nanosecondsInMinute = 60e9;
  const nanosecondsInHour = 60 * nanosecondsInMinute;
  const nanosecondsInDay = 24 * nanosecondsInHour;

  if (nanoseconds === nanosecondsInHour) {
    return '60 minutes';
  } else if (nanoseconds === nanosecondsInDay) {
    return '24 hours';
  }

  let remainingDuration = nanoseconds;
  const days = Math.floor(remainingDuration / nanosecondsInDay);
  remainingDuration -= days * nanosecondsInDay;
  const hours = Math.floor(remainingDuration / nanosecondsInHour);
  remainingDuration -= hours * nanosecondsInHour;
  const minutes = Math.floor(remainingDuration / nanosecondsInMinute);

  const parts = [];

  if (days === 1) {
    parts.push('1 day');
  } else if (days > 1) {
    parts.push(`${days} days`);
  }

  if (hours === 1) {
    parts.push('1 hour');
  } else if (hours > 1) {
    parts.push(`${hours} hours`);
  }

  if (minutes === 1) {
    parts.push('1 minute');
  } else if (minutes > 1) {
    parts.push(`${minutes} minutes`);
  }

  return parts.join(' and ');
};

export const formatPhone = (countryCallingCode?: number, phone: string = ''): string => {
  if (!countryCallingCode) {
    return phone;
  }
  return `+${countryCallingCode} ${phone}`;
};

export const enforceNumericInput = (e: FormEvent<HTMLInputElement>) => {
  const input = e.currentTarget;
  input.value = input.value.replace(/[^0-9]/g, '');
};

export const parseDateString = (value: unknown): Date => {
  if (typeof value !== 'string') {
    return value instanceof Date || typeof value === 'number' ? new Date(value) : new Date(NaN);
  }
  const text = value.trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/i.test(text)) return new Date(text);

  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3})\d*)?)?)?$/);
  if (iso) {
    const [year, month, day, hours, minutes, seconds] = iso.slice(1, 7).map((part) => Number(part ?? 0));
    const millis = Number((iso[7] ?? '').padEnd(3, '0'));
    return iso[4] === undefined
      ? new Date(Date.UTC(year, month - 1, day))
      : new Date(year, month - 1, day, hours, minutes, seconds, millis);
  }

  const named = text.match(/^(\d{1,2}) ([A-Za-z]{3}) (\d{4})(?: (\d{1,2}):(\d{2}) ([AP]M))?$/i);
  if (named) {
    const month = shortMonths.indexOf(named[2].toLowerCase());
    if (month < 0) return new Date(NaN);
    const hours12 = Number(named[4] ?? 0) % 12;
    const hours = named[6]?.toUpperCase() === 'PM' ? hours12 + 12 : hours12;
    return new Date(Number(named[3]), month, Number(named[1]), hours, Number(named[5] ?? 0));
  }

  const utcOn = text.match(/^(\d{2}):(\d{2}) UTC on (\d{1,2}) ([A-Za-z]{3}) (\d{4})$/i);
  if (utcOn) {
    const month = shortMonths.indexOf(utcOn[4].toLowerCase());
    if (month < 0) return new Date(NaN);
    return new Date(Date.UTC(Number(utcOn[5]), month, Number(utcOn[3]), Number(utcOn[1]), Number(utcOn[2])));
  }

  return new Date(NaN);
};

export const parseDate = (dateString?: string, includeTime?: boolean) => {
  if (!dateString) {
    return;
  }

  let dateParts: number[];
  let hours = 0;
  let minutes = 0;

  if (dateString.includes('T')) {
    // format '2006-01-02T00:00:00Z'
    dateParts = dateString.split('T')[0].split('-').map(Number);
    if (includeTime) {
      const timeParts = dateString.split('T')[1].split(':').map(Number);
      hours = timeParts[0];
      minutes = timeParts[1];
    }
  } else {
    // format '02 Jan 2006'
    const [day, month, year] = dateString.split(' ');
    const monthIndex = shortMonths.indexOf(month?.slice(0, 3).toLowerCase() ?? '');
    dateParts = [parseInt(year, 10), monthIndex < 0 ? NaN : monthIndex + 1, parseInt(day, 10)];
  }

  const [year, month, day] = dateParts;

  return new Date(year, month - 1, day, hours, minutes);
};

export const validateNumericInput = (e: KeyboardEvent<HTMLInputElement>) => {
  if (!/[0-9]/.test(e.key) && !['ArrowLeft', 'ArrowRight', 'Backspace', 'Delete', 'Tab'].includes(e.key)) {
    e.preventDefault();
  }
};

const tldList = TLD_LIST.split('\n').map((tld) => tld.toLowerCase());
export const validateTextMessage =
  'Input must be an appropriate length, with space after full stops and cannot contain URLs or any of these characters: < > | = //';
const isValidDomainPart = (part: string) => {
  const lastChar = part.charCodeAt(part.length - 1);
  return (lastChar >= 97 && lastChar <= 122) || (lastChar >= 48 && lastChar <= 57) || lastChar === 45;
};

export const validateTextInput = (value?: string) => {
  if (!value) {
    return true;
  }

  if (value.length > MAX_CHARACTER_LIMIT) {
    return false;
  }

  const parts = value.toLowerCase().split('.');
  for (let i = 1; i < parts.length; i++) {
    if (parts[i].trim().length > 0) {
      const part = parts[i].split(/\s+/)[0];
      if (tldList.includes(part) && isValidDomainPart(parts[i - 1])) {
        return false;
      }
    }
  }

  const invalidChars =
    value.includes('<') || value.includes('>') || value.includes('|') || value.includes('=') || value.includes('//');
  return !invalidChars;
};

const MAX_SOCIAL_MEDIA_URL_LENGTH = 2048;

const looksLikeUrl = (value: string): boolean => value.includes('//') || /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(value);

export const validateSocialMediaMessage =
  'Enter a social media handle, or a web address starting with http:// or https://. It must not contain any of these characters: < > | = ` and cannot include a username or password';

export const validateSocialMediaInput = (value?: string): boolean => {
  if (!value) {
    return true;
  }

  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f]/.test(value)) {
    return false;
  }

  if (looksLikeUrl(value)) {
    if (value.length > MAX_SOCIAL_MEDIA_URL_LENGTH) {
      return false;
    }

    if (!/^https?:\/\//i.test(value) || /\s/.test(value)) {
      return false;
    }

    let parsed: URL;
    try {
      parsed = new URL(value);
    } catch {
      return false;
    }

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return false;
    }

    if (!parsed.hostname || parsed.username || parsed.password) {
      return false;
    }

    return !/[<>|`]/.test(value);
  }

  return validateTextInput(value);
};

export const stripHtmlTags = (html: string): string => html.replace(/<[^>]*>/g, ' ').trim();

const RICH_TEXT_WORD_COUNT_CACHE_LIMIT = 250;
const richTextWordCountCache = new Map<string, number>();

const fallbackRichTextWordCount = (value?: string): number => {
  const text = stripHtmlTags(value ?? '');
  return text.length > 0 ? text.split(/\s+/).length : 0;
};

export const setRichTextTinyMceWordCount = (value: string | undefined, wordCount: number): void => {
  if (!value || !Number.isFinite(wordCount)) {
    return;
  }

  richTextWordCountCache.set(value, wordCount);

  if (richTextWordCountCache.size > RICH_TEXT_WORD_COUNT_CACHE_LIMIT) {
    const firstKey = richTextWordCountCache.keys().next().value;
    if (typeof firstKey === 'string') {
      richTextWordCountCache.delete(firstKey);
    }
  }
};

export const richTextWordCount = (value?: string): number => {
  const content = value ?? '';
  const cachedWordCount = richTextWordCountCache.get(content);

  if (typeof cachedWordCount === 'number') {
    return cachedWordCount;
  }

  return fallbackRichTextWordCount(content);
};

export const isRichTextEmpty = (value?: string): boolean => {
  return stripHtmlTags(value ?? '').length === 0;
};

export const MAX_RICH_TEXT_CHARACTER_LIMIT = 50000;

export const validateRichTextMessage = 'Rich text input must be an appropriate length.';

export const validateRichTextInput = (value?: string) => {
  if (!value) return true;
  if (value.length > MAX_RICH_TEXT_CHARACTER_LIMIT) return false;
  return true;
};

interface ErrorsInput {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface FlattenedErrors {
  label: string;
  message: string;
}

export function FlattenZodErrors(obj: ErrorsInput, parentKey: string = ''): FlattenedErrors[] {
  let result: FlattenedErrors[] = [];

  for (const key in obj) {
    const fullKey = parentKey ? `${parentKey}.${key}` : key;

    if (Array.isArray(obj[key])) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      obj[key].forEach((item: any, index: number) => {
        if (item) {
          result = result.concat(FlattenZodErrors(item, `${fullKey}.${index}`));
        }
      });
    } else if (typeof obj[key] === 'object' && obj[key] !== null && 'message' in obj[key]) {
      result.push({ label: fullKey, message: obj[key].message });
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      result = result.concat(FlattenZodErrors(obj[key], fullKey));
    }
  }

  return result;
}
